/**
 * KeyboardShortcutsHelp — tiny cheat-sheet modal.
 *
 * Toggled by the `?` key (Shift + /) anywhere except inside an editable
 * element. Lists the small set of global shortcuts the app supports.
 *
 * Lives next to the app shell because every page is wrapped in the shell
 * — one keydown listener, one modal mount.
 */

import { Modal } from '@/components/ui/modal';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface ShortcutRow {
  keys: string[];
  description: string;
}

const SHORTCUTS: ShortcutRow[] = [
  { keys: ['⌘', 'K'], description: 'Open global search' },
  { keys: ['?'], description: 'Show this help' },
  { keys: ['Esc'], description: 'Close drawer, modal, or this dialog' },
  { keys: ['⌘', '↵'], description: 'Save on edit-in-place fields (where supported)' },
];

export function KeyboardShortcutsHelp({ open, onClose }: Props) {
  return (
    <Modal open={open} onClose={onClose} title="Keyboard shortcuts" size="sm">
      <div className="space-y-2 py-2">
        {SHORTCUTS.map((row) => (
          <div
            key={row.description}
            className="flex items-center justify-between gap-4 text-sm"
          >
            <span className="text-secondary">{row.description}</span>
            <span className="flex items-center gap-1">
              {row.keys.map((k, i) => (
                <kbd
                  key={i}
                  className="rounded border border-border-default bg-base px-1.5 py-0.5 font-mono text-[11px] text-primary"
                >
                  {k}
                </kbd>
              ))}
            </span>
          </div>
        ))}
      </div>
      <p className="mt-4 border-t border-border-default pt-3 text-xs text-tertiary">
        ⌘ is the Command key on Mac, or Ctrl on Windows/Linux.
      </p>
    </Modal>
  );
}
